import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import type { TAudioPlayerElement } from '@shared/dream-components';
import { AudioPlayerSerializer } from '@shared/dream-components';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { PodcastShowEpisodePageLoaderData } from '~/routes/podcast/s/$showSlug/$episodeSlug';

import usePodcastMergeTagProcessor from './hooks/usePodcastMergeTagProcessor';

type Props = PropsWithChildren<{
  node: TAudioPlayerElement;
}>;

// TODO: Currently scoped to episode detail page only. To support the show listing
// page, make the loader data generic or detect the page context.
const AudioPlayer = ({ node, children }: Props) => {
  const { podcastEpisode } = useTypedLoaderData<PodcastShowEpisodePageLoaderData>();
  const { children: childrenToRender, node: nodeToRender } = usePodcastMergeTagProcessor({ node, children });
  const { t } = useTranslation();
  const isLoggedIn = useIsLoggedIn();
  const { handleOpenModal } = useGlobalModals();

  const handlePlayAttempt = !isLoggedIn ? () => handleOpenModal('login', { preventScrollReset: true }) : undefined;

  const isPlaybackLocked = isLoggedIn && !podcastEpisode?.audio_url;
  const tooltipText = isPlaybackLocked ? t('podcasts.episode.audio_player.locked_tooltip') : undefined;

  return (
    <AudioPlayerSerializer
      element={nodeToRender as TAudioPlayerElement}
      onPlayAttempt={handlePlayAttempt}
      isPlaybackLocked={isPlaybackLocked}
      tooltipText={tooltipText}
    >
      {childrenToRender}
    </AudioPlayerSerializer>
  );
};

export default AudioPlayer;
